import React, { useCallback, useEffect, useState } from "react";
import { Loader2, RotateCw } from "lucide-react";
import { ListTools, sortRows } from "@/components/nds/ListTools";
import { pillarApi, pillarErr, pillarDot } from "@/lib/pillarApi";
import { useAuth } from "@/lib/auth";
import { useFloatingWindows } from "@/lib/floatingWindows";
import { BoardCell, QueueButtons } from "./boardParts";
import { HowToFix } from "@/components/nds/HowToFix";
import { ArchiveViewChips, normView } from "@/components/nds/ArchiveView";
import { OpenResidentButton } from "@/components/nds/OpenResidentButton";

const KIND = { PILLAR_1_INNER: "pillar_1_inner", PILLAR_2_VOCATION: "pillar_2_vocation", PILLAR_3_SPIRIT: "pillar_3_spirit", PILLAR_4_DAILY: "pillar_4_daily" };

function rowMatches(r, queue, mine, q) {
  if (q && !r.name.toLowerCase().includes(q)) return false;
  if (mine && !r.cells.some((c) => c.mine)) return false;
  if (!queue) return true;
  if (queue === "touchpoint_due") return r.touchpoint_due;
  return r.cells.some((c) => c.state === queue && (!mine || c.mine));
}

const PGET = { name: (r) => r.name, date: (r) => r.last_touchpoint };

export default function PillarBoardContent({ win }) {
  const { user } = useAuth();
  const { openWindow } = useFloatingWindows();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [queue, setQueue] = useState(win?.payload?.queue || null);
  const [mine, setMine] = useState(false);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("urgent");
  const [view, setViewRaw] = useState(() => normView(localStorage.getItem("tea_pillar_board_view")));
  const setView = (v) => { localStorage.setItem("tea_pillar_board_view", v); setViewRaw(v); };
  const load = useCallback(() => Promise.all([pillarApi.get("/pillar-board", { view }), pillarApi.get("/pillars")])
    .then(([b, p]) => { setData({ ...b, pillars: p.pillars }); setError(null); }).catch((e) => setError(pillarErr(e))), [view]);
  useEffect(() => { load(); }, [load]);

  if (error) return <p data-testid="pillar-board-error" className="p-6 text-sm text-red-300">{error}</p>;
  if (!data) return <div data-testid="pillar-board-loading" className="p-10 flex items-center gap-2 text-nex-muted"><Loader2 className="h-4 w-4 animate-spin" /> Loading board…</div>;
  const me = (user?.email || "").toLowerCase();
  const rows = data.rows.map((r) => ({ ...r, cells: r.cells.map((c) => ({ ...c, mine: (c.lead?.email || "").toLowerCase() === me })) }));
  const shown = sortRows(rows.filter((r) => rowMatches(r, queue, mine, "")), PGET, sort, q);
  const open = (r, c) => openWindow(KIND[c.pillar_id], {
    title: data.pillars.find((p) => p.id === c.pillar_id)?.name, description: r.name,
    focus: { residentId: r.resident_id, residentName: r.name },
  });

  return (
    <div data-testid="pillar-board" className="p-5 space-y-4">
      <QueueButtons counts={data.counts} active={queue} onPick={setQueue} />
      {{ overdue: "pillar_overdue", review: "pillar_review", touchpoint_due: "touchpoint_due" }[queue] && (
        <HowToFix key={queue} code={{ overdue: "pillar_overdue", review: "pillar_review", touchpoint_due: "touchpoint_due" }[queue]} testid="fix-pillar-queue" />
      )}
      <div className="flex flex-wrap items-center gap-3">
        <ListTools prefix="pillar-board" q={q} setQ={setQ} sort={sort} setSort={setSort} get={PGET} />
        <ArchiveViewChips prefix="pillar-board" view={view} setView={setView} counts={data.view_counts} />
        <button type="button" onClick={() => setMine((m) => !m)} data-testid="pillar-board-mine" aria-pressed={mine}
          className={`px-3 py-1.5 border rounded-sm font-mono text-[10px] tracking-[0.18em] uppercase transition-colors ${mine ? "border-nex-gold text-nex-gold bg-nex-gold/10" : "border-white/15 text-nex-muted hover:text-nex-silver"}`}>
          My residents
        </button>
        {queue && <button type="button" onClick={() => setQueue(null)} data-testid="pillar-board-clear" className="font-mono text-[10px] tracking-[0.18em] uppercase text-nex-gold">Clear filter</button>}
        <span data-testid="pillar-board-count" className="font-mono text-[10px] text-nex-muted">{shown.length} of {rows.length} residents</span>
        <button type="button" onClick={load} data-testid="pillar-board-refresh" aria-label="Refresh" className="ml-auto p-1.5 text-nex-muted hover:text-nex-gold"><RotateCw className="h-3.5 w-3.5" /></button>
      </div>
      <div className="overflow-x-auto border border-white/10 rounded-sm">
        <table data-testid="pillar-board-table" className="w-full min-w-[900px] text-left">
          <thead>
            <tr className="font-mono text-[9px] tracking-[0.16em] uppercase text-nex-muted">
              <th className="px-3 py-2 font-normal">Resident</th>
              {data.pillars.map((p) => (
                <th key={p.id} className="px-2 py-2 font-normal">
                  <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: pillarDot(p.color_hex) }} />{p.name}</span>
                </th>
              ))}
              <th className="px-3 py-2 font-normal">Pace · weekly review</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.resident_id} data-testid={`pillar-board-row-${r.resident_id}`} className="border-t border-white/5 align-top">
                <td className="px-3 py-2">
                  <div className="text-sm text-nex-silver flex flex-wrap items-center gap-2">{r.name}<OpenResidentButton residentId={r.resident_id} name={r.name} />{r.archived && <span data-testid={`pillar-board-archived-${r.resident_id}`} className="ml-2 px-1.5 py-0.5 rounded-full border border-amber-400/40 text-amber-300 font-mono text-[8px] tracking-[0.18em] uppercase">Archived</span>}</div>
                  <div className="font-mono text-[10px] text-nex-muted">Day {r.program_day} · should be wk {r.expected_week}</div>
                </td>
                {data.pillars.map((p) => {
                  const c = r.cells.find((x) => x.pillar_id === p.id);
                  const dim = (queue && queue !== "touchpoint_due" && c.state !== queue) || (mine && !c.mine);
                  return <td key={p.id} className="px-2 py-2 w-[19%]"><BoardCell c={c} rid={r.resident_id} dim={dim} onOpen={() => open(r, c)} /></td>;
                })}
                <td className="px-3 py-2 whitespace-nowrap">
                  <div data-testid={`pillar-board-pace-${r.resident_id}`} className={`font-mono text-[10px] tracking-[0.14em] uppercase ${r.pace === "BEHIND" ? "text-red-300" : "text-emerald-300"}`}>
                    {r.pace === "BEHIND" ? "Behind" : "On track"}
                  </div>
                  <div data-testid={`pillar-board-tp-${r.resident_id}`} className={`text-[11px] ${r.touchpoint_due ? "text-violet-300" : "text-nex-muted"}`}>
                    {r.last_touchpoint ? `Reviewed ${r.touchpoint_days}d ago${r.last_review_by ? ` · ${r.last_review_by}` : ""}` : "Never reviewed"}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!shown.length && <p data-testid="pillar-board-empty" className="p-6 text-center text-sm text-nex-muted">Nobody matches this filter.</p>}
      </div>
    </div>
  );
}
